import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
// useAuth removed — apply flow doesn't log in directly
import '../styles/global.css';

const STEPS = ['Find Building', 'Your Unit', 'Set Up Account'];

const StepIndicator = ({ current }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 28 }}>
    {STEPS.map((label, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, flexShrink: 0,
            background: i < current ? 'var(--green)' : i === current ? 'var(--navy)' : 'var(--border)',
            color: i <= current ? 'white' : 'var(--text-sub)',
          }}>
            {i < current ? '\u2713' : i + 1}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: i === current ? 'var(--navy)' : 'var(--text-sub)', whiteSpace: 'nowrap' }}>
            {label}
          </div>
        </div>
        {i < STEPS.length - 1 && (
          <div style={{ width: 48, height: 2, background: i < current ? 'var(--green)' : 'var(--border)', margin: '0 8px', marginBottom: 20 }} />
        )}
      </div>
    ))}
  </div>
);

export default function GetStartedPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1 = welcome, 0 = address, 1 = unit, 2 = account setup
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [propertyResults, setPropertyResults] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [unitNumber, setUnitNumber] = useState('');
  const [unitCheck, setUnitCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signupForm, setSignupForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [tosAgreed, setTosAgreed] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const searchTimeout = useRef(null);

  // Address autocomplete
  const handleAddressInput = (val) => {
    setAddressQuery(val);
    setSelectedProperty(null);
    setPropertyResults([]);
    clearTimeout(searchTimeout.current);
    if (val.length < 3) { setSuggestions([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        // Search enrolled properties first
        const propRes = await api.get('/auth/tenant/search-property?q=' + encodeURIComponent(val));
        setPropertyResults(propRes.data.properties || []);
        // Also get Nominatim suggestions
        const addrRes = await api.get('/address-search?q=' + encodeURIComponent(val) + '&nyc=1');
        setSuggestions(addrRes.data || []);
      } catch { /* ignore */ }
    }, 350);
  };

  const selectProperty = (prop) => {
    setSelectedProperty(prop);
    setAddressQuery(prop.address);
    setSuggestions([]);
    setPropertyResults([]);
  };

  const selectAddress = (addr) => {
    setAddressQuery(addr);
    setSuggestions([]);
    // Check if this matches an enrolled property
    api.get('/auth/tenant/search-property?q=' + encodeURIComponent(addr))
      .then(res => {
        if (res.data.properties?.length > 0) {
          setPropertyResults(res.data.properties);
        } else {
          setPropertyResults([]);
        }
      })
      .catch(() => {});
  };

  const handleUnitCheck = async (e) => {
    e.preventDefault();
    if (!unitNumber.trim() || !selectedProperty) return;
    setLoading(true);
    try {
      const res = await api.get(`/auth/tenant/check-unit?property_id=${selectedProperty.id}&unit=${encodeURIComponent(unitNumber.trim())}`);
      setUnitCheck(res.data);
      // Pre-fill from pre-approval if exists
      if (res.data.pre_approval) {
        const pa = res.data.pre_approval;
        setSignupForm({ first_name: pa.first_name || '', last_name: pa.last_name || '', email: pa.email || '', phone: pa.phone || '' });
      }
      setStep(2);
    } catch {
      toast.error('Could not check unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const { first_name, last_name, email, phone } = signupForm;
    setLoading(true);
    try {
      await api.post('/auth/tenant/apply', {
        first_name, last_name, email: email.toLowerCase(), phone: phone || null,
        property_id: selectedProperty.id,
        unit_number: unitNumber.trim(),
      });
      setSignupDone(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showDropdown = (propertyResults.length > 0 || suggestions.length > 0) && !selectedProperty;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="topbar" role="banner">
        <Link to="/" className="topbar-logo" style={{ textDecoration: 'none' }}>
          <div className="topbar-logo-dot" aria-hidden="true" />
          Affordable Telecom LLC
        </Link>
        <div className="topbar-right">
          <Link to="/login" className="topbar-link" style={{ textDecoration: 'none' }}>Sign In</Link>
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          {/* ── Welcome Screen ── */}
          {step === -1 && (
            <div style={{ textAlign: 'center' }}>
              <div className="hero-bg" style={{ borderRadius: 16, padding: '48px 32px', marginBottom: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">🏠</div>
                <h1 style={{ color: 'white', fontSize: 26, fontWeight: 800, lineHeight: 1.2, marginBottom: 12 }}>
                  Premium internet for your community
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 15, lineHeight: 1.6, maxWidth: 380, margin: '0 auto' }}>
                  We're excited to bring premium internet to your building. Let's get you set up — it only takes a minute.
                </p>
              </div>
              <button className="btn btn-primary btn-full" style={{ fontSize: 16, padding: 15 }}
                onClick={() => setStep(0)}>
                Get Started
              </button>
              <p style={{ marginTop: 16, fontSize: 13, color: 'var(--text-sub)' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'var(--teal)', fontWeight: 600 }}>Sign in</Link>
              </p>
            </div>
          )}

          {/* ── Step 0: Address Search ── */}
          {step === 0 && (
            <>
              <StepIndicator current={0} />
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginBottom: 6 }}>
                  Find your building
                </h2>
                <p style={{ color: 'var(--text-sub)', fontSize: 14 }}>
                  Enter your building address to see if it's enrolled
                </p>
              </div>

              <div className="card" style={{ overflow: 'visible' }}>
                <div className="card-body" style={{ overflow: 'visible' }}>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label" htmlFor="building-address">Building address</label>
                    <input
                      id="building-address"
                      className="form-input"
                      placeholder="Start typing your address..."
                      value={addressQuery}
                      onChange={e => handleAddressInput(e.target.value)}
                      autoFocus
                      autoComplete="off"
                    />

                    {showDropdown && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                        background: 'white', border: '1px solid var(--border)', borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 280, overflowY: 'auto',
                      }}>
                        {propertyResults.length > 0 && (
                          <>
                            <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid var(--border)' }}>
                              Enrolled Buildings
                            </div>
                            {propertyResults.map(p => (
                              <div key={p.id} onClick={() => selectProperty(p)}
                                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--light-bg)', fontSize: 13 }}
                                onMouseOver={e => e.currentTarget.style.background = '#f0faff'}
                                onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{p.name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>{p.address}, {p.city}, {p.state}</div>
                              </div>
                            ))}
                          </>
                        )}
                        {suggestions.length > 0 && (
                          <>
                            {propertyResults.length > 0 && (
                              <div style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, color: 'var(--text-sub)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid var(--border)' }}>
                                Address Suggestions
                              </div>
                            )}
                            {suggestions.map((s, i) => (
                              <div key={i} onClick={() => selectAddress(s)}
                                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--light-bg)', fontSize: 13, color: 'var(--text)' }}
                                onMouseOver={e => e.currentTarget.style.background = '#f0faff'}
                                onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                {s}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedProperty && (
                    <div style={{ background: '#e0faf5', border: '1px solid #a0e8d5', borderRadius: 10, padding: '14px 18px', marginTop: 8 }}>
                      <div style={{ fontWeight: 700, color: '#028060', fontSize: 14, marginBottom: 2 }}>
                        {selectedProperty.name}
                      </div>
                      <div style={{ fontSize: 13, color: '#028060' }}>
                        {selectedProperty.city}, {selectedProperty.state}
                      </div>
                    </div>
                  )}

                  {addressQuery.length >= 5 && !selectedProperty && propertyResults.length === 0 && suggestions.length === 0 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', marginTop: 8 }}>
                      <div style={{ fontWeight: 600, color: '#92400e', fontSize: 13, marginBottom: 4 }}>
                        Building not found
                      </div>
                      <div style={{ fontSize: 13, color: '#92400e' }}>
                        Your building may not be enrolled yet. Contact us at{' '}
                        <a href="mailto:info@affordable-telecom.com" style={{ color: '#92400e', fontWeight: 600 }}>info@affordable-telecom.com</a>{' '}
                        to get your building set up.
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                    <button className="btn btn-outline" onClick={() => { setStep(-1); setAddressQuery(''); setSelectedProperty(null); setSuggestions([]); setPropertyResults([]); }}>
                      Back
                    </button>
                    <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
                      disabled={!selectedProperty}
                      onClick={() => setStep(1)}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 1: Unit Number ── */}
          {step === 1 && (
            <>
              <StepIndicator current={1} />
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginBottom: 6 }}>
                  What's your unit?
                </h2>
                <p style={{ color: 'var(--text-sub)', fontSize: 14 }}>
                  {selectedProperty?.name} &middot; {selectedProperty?.city}, {selectedProperty?.state}
                </p>
              </div>

              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleUnitCheck}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="unit-number">Apartment / Unit number</label>
                      <input
                        id="unit-number"
                        className="form-input"
                        placeholder="e.g. 4B, 12, PH-1"
                        value={unitNumber}
                        onChange={e => setUnitNumber(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button type="button" className="btn btn-outline" onClick={() => setStep(0)}>Back</button>
                      <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
                        disabled={!unitNumber.trim() || loading}>
                        {loading ? 'Checking...' : 'Continue'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Application Form ── */}
          {step === 2 && !signupDone && (
            <>
              <StepIndicator current={2} />
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginBottom: 6 }}>
                  {unitCheck?.account_exists ? `Welcome back!` : 'Sign up for internet service'}
                </h2>
                <p style={{ color: 'var(--text-sub)', fontSize: 14 }}>
                  {selectedProperty?.name} &middot; Unit {unitNumber}
                </p>
              </div>

              {unitCheck?.pre_approval && (
                <div style={{ background: '#e0faf5', border: '1px solid #a0e8d5', borderRadius: 10, padding: '12px 18px', marginBottom: 16, fontSize: 13, color: '#028060' }}>
                  <strong>We're expecting you!</strong> Your property manager has pre-approved your unit. We've pre-filled your information below — just confirm and submit.
                </div>
              )}

              {unitCheck?.account_exists && !unitCheck?.pre_approval && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 18px', marginBottom: 16, fontSize: 13, color: '#0369a1' }}>
                  An account already exists for this unit ({unitCheck.masked_email}). If this is you,{' '}
                  <Link to="/forgot-password" style={{ color: '#0369a1', fontWeight: 600 }}>reset your password here</Link>.
                  Otherwise, fill out the form below to apply.
                </div>
              )}

              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSignup}>
                    <div className="two-col">
                      <div className="form-group">
                        <label className="form-label" htmlFor="gs-first">First name</label>
                        <input id="gs-first" className="form-input" required autoFocus
                          value={signupForm.first_name}
                          onChange={e => setSignupForm({ ...signupForm, first_name: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="gs-last">Last name</label>
                        <input id="gs-last" className="form-input" required
                          value={signupForm.last_name}
                          onChange={e => setSignupForm({ ...signupForm, last_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="gs-email">Email address</label>
                      <input id="gs-email" type="email" className="form-input" required
                        placeholder="you@example.com"
                        value={signupForm.email}
                        onChange={e => setSignupForm({ ...signupForm, email: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="gs-phone">Phone number (optional)</label>
                      <input id="gs-phone" type="tel" className="form-input"
                        placeholder="(555) 000-0000"
                        value={signupForm.phone || ''}
                        onChange={e => setSignupForm({ ...signupForm, phone: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button type="button" className="btn btn-outline" onClick={() => { setStep(1); setUnitCheck(null); }}>Back</button>
                      <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
                        disabled={loading || !tosAgreed}>
                        {loading ? 'Submitting...' : 'Submit Application'}
                      </button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 14 }}>
                      <input type="checkbox" checked={tosAgreed} onChange={e => setTosAgreed(e.target.checked)}
                        required style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--navy)', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: 'var(--text-sub)', lineHeight: 1.5 }}>
                        I have read and agree to the{' '}
                        <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontWeight: 600 }}>Terms of Service</a> and{' '}
                        <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontWeight: 600 }}>Privacy Policy</a>.
                      </span>
                    </label>
                  </form>
                </div>
              </div>
            </>
          )}

          {/* ── Application Submitted ── */}
          {signupDone && (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: 32 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">📬</div>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', marginBottom: 8 }}>
                  {signupForm.first_name ? `We received your application, ${signupForm.first_name}!` : 'Application submitted!'}
                </h2>
                <p style={{ color: 'var(--text-sub)', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
                  We received your application for <strong>Unit {unitNumber}</strong> at <strong>{selectedProperty?.name}</strong>.
                </p>
                <p style={{ color: 'var(--text-sub)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                  We'll verify your residency within 24 hours and send you next steps to set up your internet service.
                </p>
                <div style={{ background: 'var(--light-bg)', borderRadius: 8, padding: '14px 18px', marginBottom: 20, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 6 }}>What happens next:</div>
                  <div style={{ fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.8 }}>
                    1. We verify your residency (within 24 hours)<br />
                    2. You'll receive an email to set your password<br />
                    3. Log in and choose your internet plan
                  </div>
                </div>
                <Link to="/login"><button className="btn btn-primary" style={{ width: '100%' }}>Back to Home</button></Link>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
